import { useQuery } from "react-query";
import { findExistingPrice, generateInternalProductId } from "utils/FormUtils";

import API_URL from "../../../Constants";
import { ShopContextInterface } from "../../../utils/Shop";
import { ActionLabel, JsonPrice, Price, ShopGroup, ShopWithPrices, Strain } from "../../../utils/types";
import client from "../../Client";

const fetchShopPrices = async ({ queryKey }: { queryKey: [string, string, string, boolean] }) => {
    const [, shopId, mode] = queryKey;
    const isHoreca = mode === "horeca";

    const res = await client.get(`${API_URL}/v1/shops/${shopId}?is_horeca=${isHoreca}`);

    return res.data;
};

export const filterData = (shopWithPrices: ShopWithPrices, selectedTab: string) => {
    if (selectedTab === "cannabis") {
        return shopWithPrices.prices
            .filter((price: any) => price.kind_name)
            .map((price: any) => {
                price.name = price.kind_name;

                if (price.json_prices) {
                    price.prices = {};
                    for (const p of price.json_prices) {
                        price.prices[p.label.replace(" ", "").replace("gram", "g")] = p.price;
                    }
                } else {
                    price.prices = {
                        half: price.half,
                        one: price.one,
                        two_five: price.two_five,
                        five: price.five,
                        joint: price.joint,
                        piece: price.piece,
                    };
                }

                price.description =
                    price.strains.length > 0
                        ? price.strains.map((strain: Strain) => strain.name).join(" - ")
                        : price.kind_short_description_nl;

                return price;
            });
    }

    if (selectedTab === "horeca") {
        return shopWithPrices.prices
            .filter((price: Price) => price.product_name)
            .map((price: any) => {
                price.name = price.product_name;
                price.prices = {
                    half: price.half,
                    one: price.one,
                    two_five: price.two_five,
                    five: price.five,
                    joint: price.joint,
                    piece: price.piece,
                };
                price.description = price.product_short_description_nl;
                return price;
            });
    }

    return [];
};

export const useFetchProductsPagePrices = (shopId: string, mode: string) => {
    return useQuery(["products", shopId, mode], fetchShopPrices, {
        keepPreviousData: true,
        notifyOnChangeProps: ["data"],
        staleTime: 300000,
        select: (data: ShopWithPrices) => filterData(data, mode),
        refetchOnWindowFocus: false,
        enabled: !!shopId,
    });
};

export async function updateShopToPrice(
    currentShopGroup: ShopGroup,
    cannabisJsonPrices: JsonPrice[],
    preRolledJsonPrices: JsonPrice[],
    edibleJsonPrices: JsonPrice[],
    editRecord: Price,
    actionLabels: ActionLabel[],
    shopContext: ShopContextInterface,
    internalIdPrefix: string
) {
    const prices = (await client.get(`/v1/prices?skip=0&limit=0&filter=shop_group_id%3A${currentShopGroup.id}`)).data;

    const internalProductId = generateInternalProductId(internalIdPrefix, [
        cannabisJsonPrices ?? [],
        preRolledJsonPrices ?? [],
        edibleJsonPrices ?? [],
    ]);

    const existingPrice = findExistingPrice(prices, internalProductId);

    let payload = {
        shop_id: shopContext.shopId,
        active: editRecord.active,
        new: editRecord.new,
        price_id: existingPrice ? existingPrice.id : "",
        product_id: editRecord.product_id,
        category_id: editRecord.category_id,
        kind_id: editRecord.kind_id,
        deactivated_labels: [],
        action_labels: actionLabels,
    };

    if (!existingPrice) {
        const pricePayload = {
            internal_product_id: internalProductId.substring(0, internalProductId.length - 3),
            shop_group_id: currentShopGroup.id,
            cannabis: cannabisJsonPrices,
            pre_rolled_joints: preRolledJsonPrices,
            edible: edibleJsonPrices,
        };

        await client.post(`/v1/prices`, pricePayload).then((result) => {
            payload.price_id = result.data.id;
        });
    }

    await client.put(`/v1/shops-to-prices/${editRecord.id}`, payload).then(() => {
        window.location.reload();
    });
}
